import { useState, useCallback } from 'react';
import { ClientData, ClientManagementOptions, ClientHome, HomeDetails } from '../types/business';

interface UseClientManagementReturn {
  // Data from useClients
  clients: ClientData[];
  totalClients: number;
  activeClients: number;
  isLoadingClients: boolean;
  clientsError: string | null;
  
  // Client homes data
  clientHomes: Record<string, ClientHome[]>;
  isLoadingClientHomes: boolean;
  clientHomesError: string | null;
  
  // Operations (existing)
  addClient: (clientData: {
    clientEmail: string;
    clientName?: string;
    clientPhone?: string;
    clientDetails?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  updateClient: (
    clientEmail: string,
    updates: {
      clientName?: string;
      clientEmail?: string;
      clientPhone?: string;
      clientDetails?: string;
      status?: 'active' | 'inactive' | 'pending';
    }
  ) => Promise<{ success: boolean; error?: string }>;
  deleteClient: (clientEmail: string) => Promise<{ success: boolean; error?: string }>;
  
  // Client homes operations
  fetchClientHomes: (clientEmail: string) => Promise<void>;
  
  // Loading states (existing)
  isAddingClient: boolean;
  isUpdatingClient: boolean;
  isDeletingClient: boolean;
  
  // Fetch operations
  fetchClients: () => Promise<void>;
  refreshClients: () => Promise<void>;
  
  // Utility
  transformApiClient: (apiClient: any) => ClientData;
}

export function useClientManagement({ 
  businessId, 
  agentEmail 
}: ClientManagementOptions = {}): UseClientManagementReturn {
  // State from useClients
  const [clients, setClients] = useState<ClientData[]>([]);
  const [totalClients, setTotalClients] = useState<number>(0);
  const [activeClients, setActiveClients] = useState<number>(0);
  const [isLoadingClients, setIsLoadingClients] = useState<boolean>(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  
  // Client homes state
  const [clientHomes, setClientHomes] = useState<Record<string, ClientHome[]>>({});
  const [isLoadingClientHomes, setIsLoadingClientHomes] = useState<boolean>(false);
  const [clientHomesError, setClientHomesError] = useState<string | null>(null);
  
  // State from existing useClientManagement
  const [isAddingClient, setIsAddingClient] = useState<boolean>(false);
  const [isUpdatingClient, setIsUpdatingClient] = useState<boolean>(false);
  const [isDeletingClient, setIsDeletingClient] = useState<boolean>(false);

  // Utility function from useClients
  const transformApiClient = useCallback((apiClient: any): ClientData => {
    return {
      id: apiClient.id,
      uuid: apiClient.uuid || apiClient.id,
      name: apiClient.name,
      email: apiClient.email,
      phone: apiClient.phone,
      propertyAddress: undefined,
      status: apiClient.status || 'active',
      lastActivity: new Date(apiClient.updatedAt).toLocaleDateString(),
      totalHomes: apiClient.stats?.totalHomes || 0,
      homesThisMonth: apiClient.stats?.homesThisMonth || 0,
      activeHomes: apiClient.stats?.activeHomes || 0,
      joinedDate: new Date(apiClient.createdAt).toLocaleDateString(),
      avatar: apiClient.user?.picture,
      unreadMessages: 0,
      agent: apiClient.agent ? {
        name: apiClient.agent.name,
        email: apiClient.agent.email
      } : undefined,
      // Initialize optional profile fields
      preferences: undefined,
      notes: undefined,
      stats: {
        totalHomes: apiClient.stats?.totalHomes || 0,
        homesThisMonth: apiClient.stats?.homesThisMonth || 0,
        activeHomes: apiClient.stats?.activeHomes || 0
      }
    };
  }, []);

  // Transform API home response to ClientHome
  const transformApiHome = useCallback((apiHome: any): ClientHome => {
    // Parse details if it's a string, with proper typing
    let details: HomeDetails = {};
    if (typeof apiHome.details === 'string') {
      try {
        details = JSON.parse(apiHome.details);
      } catch (e) {
        console.warn('Failed to parse home details:', apiHome.details);
        // If not valid JSON, use details as address for backward compatibility
        details = { address: apiHome.details };
      }
    } else {
      details = apiHome.details || {};
    }

    // Calculate analysis progress based on tasks
    const analysisProgress = apiHome.tasks_count > 0 
      ? Math.round((apiHome.completed_tasks_count / apiHome.tasks_count) * 100)
      : 0;

    return {
      id: apiHome.home_id,
      name: apiHome.chat_name || 'Unnamed Home',
      address: details.address || undefined,
      status: apiHome.is_deleted ? 'completed' : 'active',
      createdAt: apiHome.date_added,
      lastUpdated: apiHome.date_added, // API doesn't provide last updated, using date_added
      tasksCount: apiHome.tasks_count || 0,
      completedTasks: apiHome.completed_tasks_count || 0,
      analysisProgress
    };
  }, []);

  // Helper function to build API endpoint
  const getApiEndpoint = useCallback(() => {
    if (!businessId) return null;
    return agentEmail 
      ? `/api/business/${businessId}/${encodeURIComponent(agentEmail)}/clients`
      : `/api/business/${businessId}/clients`;
  }, [businessId, agentEmail]);

  // Helper function to build client homes API endpoint
  const getClientHomesEndpoint = useCallback((clientEmail: string) => {
    if (!businessId || !agentEmail) return null;
    return `/api/business/${businessId}/${encodeURIComponent(agentEmail)}/client/${encodeURIComponent(clientEmail)}/homes`;
  }, [businessId, agentEmail]);

  // Fetch clients function (from useClients)
  const fetchClients = useCallback(async () => {
    const endpoint = getApiEndpoint();
    if (!endpoint) {
      console.log('No business ID available for fetching clients');
      return;
    }

    setIsLoadingClients(true);
    setClientsError(null);
    
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'same-origin'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const transformedClients: ClientData[] = (data.clients || []).map(transformApiClient);
        
        setClients(transformedClients);
        setTotalClients(data.totalClients || 0);
        setActiveClients(transformedClients.filter(client => client.status === 'active').length);
      } else {
        setClientsError(data.error || 'Failed to fetch clients');
        setClients([]);
        setTotalClients(0);
        setActiveClients(0);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClientsError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setClients([]);
      setTotalClients(0);
      setActiveClients(0);
    } finally {
      setIsLoadingClients(false);
    }
  }, [getApiEndpoint, transformApiClient]);

  // Fetch client homes function
  const fetchClientHomes = useCallback(async (clientEmail: string) => {
    const endpoint = getClientHomesEndpoint(clientEmail);
    if (!endpoint) {
      console.log('No business ID or agent email available for fetching client homes');
      return;
    }

    setIsLoadingClientHomes(true);
    setClientHomesError(null);
    
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'same-origin'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const transformedHomes: ClientHome[] = (data.homes || []).map(transformApiHome);
        
        setClientHomes(prev => ({
          ...prev,
          [clientEmail]: transformedHomes
        }));
      } else {
        setClientHomesError(data.error || 'Failed to fetch client homes');
        // Set empty array for this client to indicate we tried to fetch
        setClientHomes(prev => ({
          ...prev,
          [clientEmail]: []
        }));
      }
    } catch (error) {
      console.error('Error fetching client homes:', error);
      setClientHomesError(error instanceof Error ? error.message : 'An unexpected error occurred');
      // Set empty array for this client to indicate we tried to fetch
      setClientHomes(prev => ({
        ...prev,
        [clientEmail]: []
      }));
    } finally {
      setIsLoadingClientHomes(false);
    }
  }, [getClientHomesEndpoint, transformApiHome]);

  // Enhanced CRUD operations that auto-refresh
  const addClient = useCallback(async (clientData: {
    clientEmail: string;
    clientName?: string;
    clientPhone?: string;
    clientDetails?: string;
  }): Promise<{ success: boolean; error?: string }> => {
    const endpoint = getApiEndpoint();
    if (!endpoint) {
      return { success: false, error: 'No business ID or agent email available' };
    }

    setIsAddingClient(true);
    
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(clientData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Auto-refresh clients list
        await fetchClients();
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to add client' };
      }
    } catch (error) {
      console.error('Error adding client:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsAddingClient(false);
    }
  }, [getApiEndpoint, fetchClients]);

  const updateClient = useCallback(async (
    clientEmail: string,
    updates: {
      clientName?: string;
      clientEmail?: string;
      clientPhone?: string;
      clientDetails?: string;
      status?: 'active' | 'inactive' | 'pending';
    }
  ): Promise<{ success: boolean; error?: string }> => {
    if (!businessId || !agentEmail) {
      return { success: false, error: 'No business ID or agent email available' };
    }

    setIsUpdatingClient(true);
    
    try {
      const response = await fetch(`/api/business/${businessId}/${encodeURIComponent(agentEmail)}/clients`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ clientEmail, updates })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Auto-refresh clients list
        await fetchClients();
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to update client' };
      }
    } catch (error) {
      console.error('Error updating client:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsUpdatingClient(false);
    }
  }, [businessId, agentEmail, fetchClients]);

  const deleteClient = useCallback(async (clientEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!businessId || !agentEmail) {
      return { success: false, error: 'No business ID or agent email available' };
    }

    setIsDeletingClient(true);
    
    try {
      const response = await fetch(`/api/business/${businessId}/${encodeURIComponent(agentEmail)}/clients`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ clientEmail })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Auto-refresh clients list
        await fetchClients();
        // Also clear cached homes for this client
        setClientHomes(prev => {
          const newHomes = { ...prev };
          delete newHomes[clientEmail];
          return newHomes;
        });
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to delete client' };
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsDeletingClient(false);
    }
  }, [businessId, agentEmail, fetchClients]);

  const refreshClients = fetchClients;

  return {
    // Data
    clients,
    totalClients,
    activeClients,
    isLoadingClients,
    clientsError,
    
    // Client homes data
    clientHomes,
    isLoadingClientHomes,
    clientHomesError,
    
    // Operations
    addClient,
    updateClient,
    deleteClient,
    
    // Client homes operations
    fetchClientHomes,
    
    // Loading states
    isAddingClient,
    isUpdatingClient,
    isDeletingClient,
    
    // Fetch operations
    fetchClients,
    refreshClients,
    
    // Utility
    transformApiClient
  };
}